import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AtomicBlockUtils,
    Editor,
    EditorState,
    RichUtils,
    SelectionState,
    getDefaultKeyBinding,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";

import { AtomicImage } from "./atomicImage";
import { createLinkDecorator } from "./linkDecorator";
import { getTouchedLinkRanges } from "./linkRanges";
import {
    removeAtomicByBackspace,
    removeAtomicByDelete,
    shouldHandleAtomicBackspace as hasAtomicBackspaceTarget,
    shouldHandleAtomicDelete as hasAtomicDeleteTarget,
} from "./atomicCommands";
import { TextEditorToolbar } from "./textEditorToolbar";
import { LinkPopup } from "./linkPopup";

import styles from "./textEditor.module.css";

import { Text } from "../text/text";

export const TextEditor = ({ placeholder, state, setState }) => {
    const [isLinkPopupActive, setIsLinkPopupActive] = useState(false);
    const [isImageLinkPopupActive, setIsImageLinkPopupActive] = useState(false);
    const [linkUrl, setLinkUrl] = useState("https://");
    const [pendingSelection, setPendingSelection] = useState(null);

    const toggleBlockType = useCallback(
        (type) => {
            setState((currentState) => RichUtils.toggleBlockType(currentState, type));
        },
        [setState]
    );

    const currentBlockType = useMemo(() => {
        const selection = state.getSelection();
        const content = state.getCurrentContent();
        const block = content.getBlockForKey(selection.getStartKey());
        return block.getType();
    }, [state]);

    const toggleInlineBlockStyle = useCallback(
        (type) => {
            setState((currentState) => RichUtils.toggleInlineStyle(currentState, type));
        },
        [setState]
    );

    const hasInlineStyle = useCallback(
        (inlineStyle) => {
            const currentStyle = state.getCurrentInlineStyle();
            return currentStyle.has(inlineStyle);
        },
        [state]
    );

    const hasLinkInSelection = useMemo(
        () => getTouchedLinkRanges(state).length > 0,
        [state]
    );

    const closeLinkPopup = useCallback(() => {
        setIsLinkPopupActive(false);
        setPendingSelection(null);
        setLinkUrl("https://");
    }, []);

    const closeImageLinkPopup = useCallback(() => {
        setIsImageLinkPopupActive(false);
        setLinkUrl("https://");
    }, []);

    const openLinkPopup = useCallback((selection) => {
        setPendingSelection(selection);
        setLinkUrl("https://");
        setIsLinkPopupActive(true);
    }, []);

    const openImageLinkPopup = useCallback((selection) => {
        setLinkUrl("https://");
        setIsImageLinkPopupActive(true);
    }, []);

    const applyLinkFromPopup = useCallback(() => {
        if (!pendingSelection) return;

        const trimmedUrl = linkUrl.trim();
        if (!trimmedUrl) return;

        const normalizedUrl = /^https?:\/\//i.test(trimmedUrl)
            ? trimmedUrl
            : `https://${trimmedUrl}`;

        const contentState = state.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", {
            url: normalizedUrl,
            targetOption: "_blank",
        });

        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const contentUpdatedState = EditorState.set(state, {
            currentContent: contentStateWithEntity,
        });

        const selectionState = pendingSelection;
        const selectedState = EditorState.forceSelection(contentUpdatedState, selectionState);

        setState(RichUtils.toggleLink(selectedState, selectionState, entityKey));
        closeLinkPopup();
    }, [pendingSelection, linkUrl, state, setState, closeLinkPopup]);

    const toggleLinkFromSelection = useCallback(() => {
        const touchedLinkRanges = getTouchedLinkRanges(state);

        if (touchedLinkRanges.length > 0) {
            let nextState = state;

            touchedLinkRanges.forEach(({ blockKey, start, end }) => {
                const entitySelection = SelectionState.createEmpty(blockKey).merge({
                    anchorOffset: start,
                    focusOffset: end,
                });

                nextState = RichUtils.toggleLink(nextState, entitySelection, null);
            });

            setState(nextState);
            return;
        }

        const selection = state.getSelection();

        if (selection.isCollapsed()) return;

        openLinkPopup(selection);
    }, [state, setState, openLinkPopup]);

    const deleteAtomicByBackspace = useCallback(() => {
        const nextState = removeAtomicByBackspace(state);
        if (!nextState) return false;

        setState(nextState);
        return true;
    }, [state, setState]);

    const deleteAtomicByDelete = useCallback(() => {
        const nextState = removeAtomicByDelete(state);
        if (!nextState) return false;

        setState(nextState);
        return true;
    }, [state, setState]);

    const keyBindingFn = useCallback((e) => {
        if (e.key === "Backspace" && hasAtomicBackspaceTarget(state)) return "editor-backspace";
        if (e.key === "Delete" && hasAtomicDeleteTarget(state)) return "editor-delete";
        return getDefaultKeyBinding(e);
    }, [state]);

    const handleKeyCommand = useCallback((command) => {
        if (command === "editor-backspace") {
            return deleteAtomicByBackspace() ? "handled" : "not-handled";
        }

        if (command === "editor-delete") {
            return deleteAtomicByDelete() ? "handled" : "not-handled";
        }

        return "not-handled";
    }, [deleteAtomicByBackspace, deleteAtomicByDelete]);

    const insertImage = useCallback((src) => {
        const contentState = state.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", { src });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        const newEditorState = AtomicBlockUtils.insertAtomicBlock(
            state,
            entityKey,
            "\n"
        );

        setState(EditorState.forceSelection(
            newEditorState,
            newEditorState.getCurrentContent().getSelectionAfter()
        ));
    }, [state, setState]);

    const applyImageLink = useCallback(() => {
        insertImage(linkUrl);
        closeImageLinkPopup();
    }, [closeImageLinkPopup, insertImage, linkUrl]);

    const customRenderer = (block) => {
        if (block.getType() === "atomic") {
            if (!block.getEntityAt(0)) return null;
            return {
                component: AtomicImage,
                editable: false,
            };
        }
        return null;
    }

    const editorRef = useRef(null);

    return (
        <div className={styles.text_editor_container}>
            {
                placeholder &&
                <Text as="div" variant="caption" className={styles.label} >{placeholder}</Text>
            }
            <div className={styles.text_editor}>
                <TextEditorToolbar
                    currentBlockType={currentBlockType}
                    hasInlineStyle={hasInlineStyle}
                    hasLinkInSelection={hasLinkInSelection}
                    onToggleBlockType={toggleBlockType}
                    onToggleInlineStyle={toggleInlineBlockStyle}
                    onToggleLink={toggleLinkFromSelection}
                    onInsertImage={openImageLinkPopup}
                />

                <div
                    className={styles.editor_container}
                    onClick={() => {
                        editorRef.current?.focus();
                    }}
                >
                    <Editor
                        blockRendererFn={customRenderer}
                        keyBindingFn={keyBindingFn}
                        handleKeyCommand={handleKeyCommand}
                        editorState={state}
                        onChange={setState}
                        ref={editorRef}
                    />
                </div>
            </div>

            <LinkPopup
                title="Добавить ссылку"
                description="Вставьте URL для выделенного текста"
                active={isLinkPopupActive}
                setActive={setIsLinkPopupActive}
                linkUrl={linkUrl}
                setLinkUrl={setLinkUrl}
                onCancel={closeLinkPopup}
                onApply={applyLinkFromPopup}
            />

            <LinkPopup
                title="Добавить изображение"
                description="Вставьте URL изображения"
                active={isImageLinkPopupActive}
                setActive={openImageLinkPopup}
                linkUrl={linkUrl}
                setLinkUrl={setLinkUrl}
                onCancel={closeImageLinkPopup}
                onApply={applyImageLink}
            />
        </div>
    );
};

export const useTextEditor = (placeholder, html = null) => {
    const decorator = useMemo(() => createLinkDecorator(), []);

    const createStateFromHtml = useCallback((nextHtml) => {
        if (!nextHtml || (typeof nextHtml === "string" && nextHtml.trim() === "")) {
            return EditorState.createEmpty(decorator);
        }

        return EditorState.createWithContent(stateFromHTML(nextHtml), decorator);
    }, [decorator]);

    const [state, setState] = useState(() => createStateFromHtml(html));

    useEffect(() => {
        if (html === null) return;
        setState(createStateFromHtml(html));
    }, [html, createStateFromHtml]);

    const saveHtml = useCallback((state) => {
        const rawHtml = stateToHTML(
            state.getCurrentContent(),
            {
                entityStyleFn: (entity) => {
                    if (entity.type === 'IMAGE') {
                        return {
                            element: 'img',
                            attributes: { src: entity.data.src, width: "100%", height: "auto" },
                            style: { borderRadius: "14px", display: "block", margin: "14px 0" },
                        };
                    }

                    if (entity.type === "LINK") {
                        return {
                            element: "a",
                            attributes: {
                                href: entity.data.url,
                                class: "link",
                                target: "_blank",
                                rel: "noopener noreferrer",
                            },
                        };
                    }
                }
            }
        );

        // return autoLinkifyHttpLines(rawHtml);
        return rawHtml;
    }, []);

    const setHtml = useCallback((nextHtml) => {
        setState(createStateFromHtml(nextHtml));
    }, [createStateFromHtml]);

    const editor = useMemo(
        () => (
            <TextEditor state={state} setState={setState} placeholder={placeholder} />
        ),
        [state, placeholder]
    );

    const toHtml = useCallback(() => saveHtml(state), [state, saveHtml]);

    return [editor, toHtml, setHtml, state];
};